/**
 * Shared types for Homepage CMS JSON fields (Who cards + compare rows).
 */

export type HomepageWhoImageKey = 'peFirms' | 'peBacked' | 'consultants';

export interface HomepageWhoCard {
  id: string;
  title: string;
  descLines: string[];
  roles: string;
  imageKey: HomepageWhoImageKey;
}

export interface HomepageCompareRow {
  label: string;
  others: string;
}

export function parseWhoCards(raw: string | undefined | null): HomepageWhoCard[] | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row, index) => {
        const imageKey = row.imageKey;
        const safeImage: HomepageWhoImageKey =
          imageKey === 'peFirms' || imageKey === 'peBacked' || imageKey === 'consultants'
            ? imageKey
            : 'peFirms';
        const descLines = Array.isArray(row.descLines)
          ? row.descLines.map((d) => String(d ?? '').trim()).filter(Boolean)
          : [];
        return {
          id: String(row.id || `who-${index + 1}`),
          title: String(row.title || '').trim(),
          descLines: descLines.length > 0 ? descLines : [''],
          roles: String(row.roles || '').trim(),
          imageKey: safeImage,
        };
      })
      .filter((c) => c.title);
  } catch {
    return null;
  }
}

export function parseCompareRows(raw: string | undefined | null): HomepageCompareRow[] | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row) => ({
        label: String(row.label || '').trim(),
        others: String(row.others || '').trim(),
      }))
      .filter((r) => r.label);
  } catch {
    return null;
  }
}

export const DEFAULT_WHO_CARDS: HomepageWhoCard[] = [
  {
    id: 'peFirms',
    title: 'Private Equity Firms',
    descLines: ['Portfolio wide visibility', 'Standard playbooks'],
    roles: 'PE Operating Partners · Portfolio CxOs',
    imageKey: 'peFirms',
  },
  {
    id: 'peBacked',
    title: 'Public or PE Backed Companies',
    descLines: ['Value creation focus & alignment', 'Rapid Execution & Risk Management'],
    roles: 'Chief Transformation Officers · VP Transformation · CxOs',
    imageKey: 'peBacked',
  },
  {
    id: 'consultants',
    title: 'Transformation Consultants',
    descLines: ['Digitize and Monetize IP', 'Client outreach & engagement'],
    roles: 'Transformation Partners · Practice Leaders',
    imageKey: 'consultants',
  },
];

export const DEFAULT_COMPARE_ROWS: HomepageCompareRow[] = [
  { label: 'Multi-tenant SaaS', others: 'Mostly no' },
  { label: 'Self-service', others: 'Sometimes' },
  { label: 'AI-assisted planning', others: 'Sometimes' },
  { label: 'Roadmap marketplace', others: 'Mostly no' },
  { label: 'Full transformation OS (incl. risk)', others: 'Rare' },
];
